import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Component for managing page-specific meta tags
 * Updates document title and meta tags dynamically
 */
export default function SEO({ 
  title, 
  description, 
  keywords,
  canonical,
  ogImage,
  noindex = false 
}) {
  const location = useLocation();
  const baseUrl = 'https://resumesurgeon.net';
  
  useEffect(() => {
    // Set document title
    document.title = title || 'Resume Surgeon - Professional Resume Rewrite Service';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description || 'Professional resume rewrite service. Every resume is reviewed by a real person before invoicing. ATS-friendly formatting, expert writing, and reasonable revisions within scope.');
    }
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogImg = document.querySelector('meta[property="og:image"]');
    
    if (ogTitle) ogTitle.setAttribute('content', title || 'Resume Surgeon');
    if (ogDesc) ogDesc.setAttribute('content', description || 'Professional resume rewrite service');
    if (ogImage && ogImg) ogImg.setAttribute('content', ogImage);
    
    // Update Twitter tags
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    
    if (twTitle) twTitle.setAttribute('content', title || 'Resume Surgeon');
    if (twDesc) twDesc.setAttribute('content', description || 'Professional resume rewrite service');
    
    // Handle canonical URL
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    const canonicalUrl = canonical || `${baseUrl}${location.pathname}`;
    
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonicalUrl);
    
    // Handle keywords
    let keywordsEl = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      if (!keywordsEl) {
        keywordsEl = document.createElement('meta');
        keywordsEl.setAttribute('name', 'keywords');
        document.head.appendChild(keywordsEl);
      }
      keywordsEl.setAttribute('content', keywords);
    }
    
    // Handle noindex
    let robotsEl = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robotsEl) {
        robotsEl = document.createElement('meta');
        robotsEl.setAttribute('name', 'robots');
        document.head.appendChild(robotsEl);
      }
      robotsEl.setAttribute('content', 'noindex, nofollow');
    } else if (robotsEl) {
      robotsEl.setAttribute('content', 'index, follow');
    }
    
    // Cleanup
    return () => {
      // Reset to defaults on unmount if needed
    };
  }, [title, description, keywords, canonical, ogImage, noindex, location.pathname]);
  
  return null;
}

// SEO data for common pages
export const SEO_DATA = {
  home: {
    title: 'Resume Surgeon - Professional Resume Rewrite Service | ATS-Friendly',
    description: 'Professional resume rewrite service with real human review. Every resume is reviewed before invoicing. ATS-friendly formatting, expert writing, and revisions included within scope. Start your intake today.',
    keywords: 'resume rewrite service, professional resume help, ATS-friendly resume, resume editing, career change resume, job seeker resume help'
  },
  pricing: {
    title: 'Resume Service Pricing | Starting at $59 | Resume Surgeon',
    description: 'Resume services starting at $59. Every project personally reviewed before scope and pricing are confirmed. Resume rewrite, LinkedIn optimization, cover letter support. No charge until scope confirmed.',
    keywords: 'resume rewrite cost, resume service pricing, professional resume price, LinkedIn optimization cost'
  },
  faq: {
    title: 'FAQ - How Resume Surgeon Works | Questions Answered',
    description: 'Frequently asked questions about Resume Surgeon. Learn about our intake-first process, how payment works, what ATS-friendly means, revision policy, and turnaround times.',
    keywords: 'resume service FAQ, resume rewrite questions, ATS resume help, resume revision policy'
  },
  samples: {
    title: 'Resume Before & After Examples | Real Transformations | Resume Surgeon',
    description: 'See real resume transformations. Before and after examples showing how Resume Surgeon improves ATS compatibility, formatting, and professional presentation.',
    keywords: 'resume before after examples, resume transformation, resume rewrite samples'
  },
  start: {
    title: 'Start Your Resume Intake | Resume Surgeon',
    description: 'Submit your resume for professional review. Tell us about your goals and let us confirm the right service for you. No payment until scope is confirmed.',
    keywords: 'submit resume for review, start resume rewrite, resume intake form'
  },
  audit: {
    title: 'Free Resume Quick Audit | Resume Surgeon',
    description: 'Get a free quick audit of your resume. Our experts will review your resume and provide initial feedback. No obligation, no payment required.',
    keywords: 'free resume review, resume audit, resume feedback'
  },
  // Service pages
  resumeRewrite: {
    title: 'Professional Resume Rewrite Service | ATS-Friendly | Resume Surgeon',
    description: 'Complete resume rewrite service for job seekers. ATS-friendly formatting, achievement-focused bullets, keyword optimization. Every resume reviewed before invoicing.',
    keywords: 'resume rewrite service, professional resume rewrite, ATS resume rewrite, resume from scratch'
  },
  resumeRefresh: {
    title: 'Resume Refresh & Editing Service | Quick Updates | Resume Surgeon',
    description: 'Resume refresh and editing for minor updates. Fix formatting issues, improve clarity, update recent experience. Fast turnaround, affordable pricing.',
    keywords: 'resume editing service, resume refresh, resume update, resume cleanup'
  },
  linkedIn: {
    title: 'LinkedIn Profile Optimization Service | Resume Surgeon',
    description: 'Professional LinkedIn optimization service. Compelling headline, keyword-rich About section, profile tips. Improve your visibility to recruiters.',
    keywords: 'LinkedIn optimization, LinkedIn profile help, LinkedIn headline, LinkedIn About section'
  },
  coverLetter: {
    title: 'Cover Letter Writing Service | Resume Surgeon',
    description: 'Professional cover letter writing and templates. Tailored to your target role, easy to customize. Complements your resume rewrite.',
    keywords: 'cover letter service, cover letter writing, professional cover letter, cover letter template'
  },
  resumeReview: {
    title: 'Resume Review & Audit Service | Expert Feedback | Resume Surgeon',
    description: 'Get expert feedback on your resume. We review your current resume and identify areas for improvement. Understand what\'s working and what needs help.',
    keywords: 'resume review service, resume audit, resume feedback, resume critique'
  },
  // Niche pages
  customerService: {
    title: 'Resume Help for Customer Service Jobs | Resume Surgeon',
    description: 'Resume rewrite service for customer service professionals. Highlight communication skills, problem-solving, and customer satisfaction achievements.',
    keywords: 'customer service resume, support representative resume, call center resume, help desk resume'
  },
  officeAdmin: {
    title: 'Resume Help for Office & Administrative Jobs | Resume Surgeon',
    description: 'Resume rewrite service for office and administrative professionals. Showcase organization, attention to detail, and office software skills.',
    keywords: 'office resume, administrative assistant resume, admin resume, office job resume'
  },
  careerChange: {
    title: 'Resume Help for Career Changers | Resume Surgeon',
    description: 'Resume rewrite service for career changers. Reposition transferable skills, highlight relevant experience, target new industries effectively.',
    keywords: 'career change resume, career transition resume, switching careers resume, new career resume'
  },
  entryLevel: {
    title: 'Resume Help for Entry-Level Job Seekers | Resume Surgeon',
    description: 'Resume rewrite service for entry-level candidates. Maximize limited experience, highlight education and skills, position for first career roles.',
    keywords: 'entry level resume, new graduate resume, first job resume, limited experience resume'
  },
  operations: {
    title: 'Resume Help for Operations & Technician Roles | Resume Surgeon',
    description: 'Resume rewrite service for operations and technical professionals. Highlight certifications, technical skills, and process improvements.',
    keywords: 'operations resume, technician resume, manufacturing resume, production resume'
  }
};
